import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const NewsContext = createContext();

export const useNews = () => {
    const context = useContext(NewsContext);
    if (!context) {
        throw new Error('useNews must be used within a NewsProvider');
    }
    return context;
};

export const NewsProvider = ({ children }) => {
    const [latestArticle, setLatestArticle] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Check if user is authenticated
    const isAuthenticated = () => {
        const token = localStorage.getItem('token');
        return token && !isTokenExpired(token);
    };

    // Simple token expiration check
    const isTokenExpired = (token) => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    };

    // Fetch latest article for notifications
    const fetchLatestArticle = async () => {
        if (!isAuthenticated()) return;

        try {
            setIsLoading(true);
            const response = await api.getLatestNewsArticle();
            
            if (response.data && response.data.article && !response.data.isDismissed) {
                // Only show notification if it's a different article or if no notification is currently shown
                if (!latestArticle || latestArticle._id !== response.data.article._id) {
                    setLatestArticle(response.data.article);
                    setShowNotification(true);
                }
            } else {
                // Article is dismissed or no article available
                setShowNotification(false);
                setLatestArticle(null);
            }
        } catch (error) {
            console.error('Error fetching latest article:', error);
            setShowNotification(false);
            setLatestArticle(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Dismiss article notification
    const dismissArticle = async (articleId) => {
        try {
            await api.dismissNewsArticle(articleId);
            // Clear the notification immediately
            setShowNotification(false);
            setLatestArticle(null);
        } catch (error) {
            console.error('Error dismissing article:', error);
            // Even if the API call fails, hide the notification to prevent user frustration
            setShowNotification(false);
            setLatestArticle(null);
        }
    };

    // Open article in new tab and dismiss notification
    const openArticle = async () => {
        if (latestArticle) {
            window.open('/news', '_blank');
            // Dismiss the notification after opening the article
            await dismissArticle(latestArticle._id);
        }
    };

    // Check for new articles on mount and periodically
    useEffect(() => {
        fetchLatestArticle();
        
        // Only poll if user is authenticated and no notification is currently shown
        const shouldPoll = () => {
            return isAuthenticated() && !showNotification && !document.hidden;
        };
        
        // Check for new articles every 30 minutes (reduced from 5 minutes)
        // Only poll if user is active, no notification is shown, and tab is visible
        const interval = setInterval(() => {
            if (shouldPoll()) {
                fetchLatestArticle();
            }
        }, 30 * 60 * 1000);
        
        return () => clearInterval(interval);
    }, [showNotification]);

    const value = {
        latestArticle,
        showNotification,
        isLoading,
        dismissArticle,
        openArticle,
        fetchLatestArticle
    };

    return (
        <NewsContext.Provider value={value}>
            {children}
        </NewsContext.Provider>
    );
};
